import React, { useState, useEffect } from 'react';
import { Card, Form, Row, Col, Select, Button, Spin } from 'antd';
import {
    OtherDRBranchWise
} from './SavingService';
import { errHandler } from '../../helper/helper';
import { exportExcelBranchWise } from "../../Utils/OtherDepositsExport";

const { Option } = Select;

const OtherDReport = ({
    loginPlaceId,
    loading,
    products,
    districtData,
    districtName,
    divisionalId,
    branchId
}) => {
    const [selectedDistricts, setSelectedDistricts] = useState([]);
    const [selectedDivisionalAreas, setSelectedDivisionalAreas] = useState([]);
    const [selectedBranches, setSelectedBranches] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [availableDivisionalAreas, setAvailableDivisionalAreas] = useState([]);
    const [availableBranches, setAvailableBranches] = useState([]);
    const [reportLoading, setReportLoading] = useState(false);

    const activeStateOptions = [
        { value: 0, label: '🟢 Active' },
        { value: 1, label: '🔴 Dormant' },
        { value: 2, label: '⚪ All' }
    ];
    const [selectedActiveState, setSelectedActiveState] = useState(0);

    // Update available divisional areas based on selected districts
    useEffect(() => {
        let filteredDivisionalAreas = [];

        if (loginPlaceId === "1") {
            // Branch-level user: no divisional area selection needed
            // They will have their branch auto-selected
            filteredDivisionalAreas = [];
        } else if (loginPlaceId === "2" && divisionalId) {
            // Divisional-level user: find the matching divisional area from any district
            filteredDivisionalAreas = districtData
                .flatMap(d => d.divisionalAreaDtoList || [])
                .filter(div => div.divAId === divisionalId);

            setSelectedDivisionalAreas([divisionalId]); // auto-select the one they're assigned to
        } else if (loginPlaceId === "4") {
            // District-level user: filter by their district
            filteredDivisionalAreas = districtData
                .filter(d => d.districtName === districtName)
                .flatMap(d => d.divisionalAreaDtoList || []);
        } else if (loginPlaceId === "3") {
            // Island-level user: based on selected districts
            filteredDivisionalAreas = districtData
                .filter(d => selectedDistricts.includes(d.districtId))
                .flatMap(d => d.divisionalAreaDtoList || []);

            // Clean up selected divisional areas
            setSelectedDivisionalAreas(prev =>
                prev.filter(did =>
                    filteredDivisionalAreas.some(d => d.divAId === did)
                )
            );
        }

        setAvailableDivisionalAreas(filteredDivisionalAreas);
    }, [selectedDistricts, districtData, loginPlaceId, divisionalId, districtName]);

    // Update available branches based on selected divisional areas
    useEffect(() => {
        if (loginPlaceId === "1" && branchId) {
            // Branch-level user: auto-select their branch
            const branchIdNum = parseInt(branchId);
            setSelectedBranches([branchIdNum]);
            setAvailableBranches([{ id: branchIdNum, branchName: "Your Branch" }]); // Placeholder
            return;
        }

        const filteredBranches = districtData
            .flatMap(d => d.divisionalAreaDtoList || [])
            .filter(div => selectedDivisionalAreas.includes(div.divAId))
            .flatMap(div => div.branchDtoList || []);

        setAvailableBranches(filteredBranches);

        // Clean up selected branches
        setSelectedBranches(prev =>
            prev.filter(bid =>
                filteredBranches.some(b => b.id === bid)
            )
        );
    }, [selectedDivisionalAreas, districtData, loginPlaceId, branchId]);

    const generateBranchReport = async () => {
        let data = {
            branchIdList: selectedBranches,
            productIdList: selectedProducts,
            activeStatus: selectedActiveState
        }
        try {
            const res = await OtherDRBranchWise(data);
            return res;
        } catch (err) {
            errHandler(err);
            throw err;
        }
    }

    const handleExport = async () => {
        if (selectedBranches.length === 0) {
            errHandler("Please select at least one branch");
            return;
        }

        if (selectedProducts.length === 0) {
            errHandler("Please select at least one product");
            return;
        }

        setReportLoading(true);
        try {
            const apiData = await generateBranchReport();
            const hasData = apiData?.data?.length > 0;

            if (hasData) {
                await exportExcelBranchWise({
                    apiData: apiData.data,
                    fileName: "Branch_Wise_Other_Deposits_Report.xlsx"
                });
            } else {
                errHandler("No data available to export.");
            }
        } catch (err) {
            errHandler(err);
        } finally {
            setReportLoading(false);
        }
    };

    return (
        <div className="panel-container">
            <Card hoverable className="inner-card">
                <Form layout="vertical">
                    <Row gutter={[16, 16]}>

                        <Col span={24}>
                            <Form.Item label="Select Active State">
                                <Select
                                    placeholder="Select active state"
                                    style={{ width: '100%' }}
                                    value={selectedActiveState}
                                    onChange={setSelectedActiveState}
                                    allowClear
                                >
                                    {activeStateOptions.map(option => (
                                        <Option key={option.value} value={option.value}>
                                            {option.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        {/* Island users (loginPlaceId === "3") - Show district selection */}
                        {loginPlaceId === "3" && (
                            <Col span={24}>
                                <Form.Item label="Select Districts">
                                    <Select
                                        mode="multiple"
                                        placeholder="Select districts"
                                        style={{ width: '100%' }}
                                        value={selectedDistricts}
                                        onChange={(values) => {
                                            if (values.includes('ALL')) {
                                                setSelectedDistricts(districtData.map(d => d.districtId));
                                            } else {
                                                setSelectedDistricts(values);
                                            }
                                        }}
                                    >
                                        <Option key="ALL" value="ALL">
                                            🔘 Select All Districts
                                        </Option>
                                        {districtData.map(d => (
                                            <Option key={d.districtId} value={d.districtId}>
                                                🏙️ {d.districtName}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        {/* Island & District users - Show divisional area selection */}
                        {(loginPlaceId === "3" || loginPlaceId === "4" || loginPlaceId === "2") && (
                            <Col span={24}>
                                <Form.Item label="Select Divisional Areas">
                                    <Select
                                        mode="multiple"
                                        placeholder="Select divisional areas"
                                        style={{ width: '100%' }}
                                        value={selectedDivisionalAreas}
                                        onChange={(values) => {
                                            if (values.includes('ALL')) {
                                                setSelectedDivisionalAreas(availableDivisionalAreas.map(d => d.divAId));
                                            } else {
                                                setSelectedDivisionalAreas(values);
                                            }
                                        }}
                                        disabled={loginPlaceId === "2"} // Disabled for divisional users as it's auto-selected
                                    >
                                        <Option key="ALL" value="ALL">
                                            🔘 Select All Divisional Areas
                                        </Option>
                                        {availableDivisionalAreas.map(d => (
                                            <Option key={d.divAId} value={d.divAId}>
                                                🏙️ {d.divAIdName}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        {/* All users except branch - Show branch selection */}
                        {loginPlaceId !== "1" && (
                            <Col span={24}>
                                <Form.Item label="Select Branch(es)">
                                    <Select
                                        mode="multiple"
                                        placeholder="Select branches"
                                        style={{ width: '100%' }}
                                        value={selectedBranches}
                                        onChange={(values) => {
                                            if (values.includes('ALL')) {
                                                setSelectedBranches(availableBranches.map(b => b.id));
                                            } else {
                                                setSelectedBranches(values);
                                            }
                                        }}
                                        disabled={availableBranches.length === 0}
                                    >
                                        <Option key="ALL" value="ALL">
                                            🔘 Select All Branches
                                        </Option>
                                        {availableBranches.map(branch => (
                                            <Option key={branch.id} value={branch.id}>
                                                🏢 {branch.branchName}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        {/* Multi-select product */}
                        <Col span={24}>
                            <Form.Item label="Select Products">
                                <Select
                                    mode="multiple"
                                    allowClear
                                    placeholder="Select products"
                                    style={{ width: '100%' }}
                                    value={selectedProducts}
                                    onChange={(values) => {
                                        if (values.includes('ALL')) {
                                            setSelectedProducts(
                                                products
                                                    .filter(p => p.productId !== 1 && p.productId !== 2)
                                                    .map(b => b.productId)
                                            );
                                        } else {
                                            setSelectedProducts(values);
                                        }
                                    }}
                                >
                                    <Option key="ALL" value="ALL">
                                        🔘 Select All Products
                                    </Option>

                                    {products
                                        .filter(p => p.productId !== 1 && p.productId !== 2)
                                        .map(product => (
                                            <Option key={product.productId} value={product.productId}>
                                                {product.productName}
                                            </Option>
                                        ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        {/* Submit button */}
                        <Col span={24}>
                            <Form.Item className="generate-btn-outline">
                                <Button
                                    onClick={handleExport}
                                    disabled={reportLoading}
                                    className="generate-btn"
                                    style={{ textAlign: 'right' }}
                                >
                                    {reportLoading ? <Spin /> : 'Generate Report'}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>
        </div>
    );
};

export default OtherDReport;